/*
 * Глобальное состояние приложения (Zustand). Держит сессию (токен в памяти),
 * маршрут (segment-control), снимок движка (из WS), статусы соединения/активности.
 * Сокет и подписка на активность API живут в модульных переменных (не в сторе —
 * чтобы не были реактивными), а их события — пишутся в стор.
 */

import { create } from 'zustand'

import {
  ApiError,
  getExchanges,
  getStatus,
  setAuthToken,
  subscribeActivity,
} from '@/lib/api'
import { EngineSocket, type WsStatus } from '@/lib/ws'
import {
  clearSession,
  hasStoredSession,
  loadRoute,
  loadSession,
  saveRoute,
  saveSession,
} from '@/lib/session'
import type { AlertItem, EngineState } from '@/lib/types'

export type AppRoute = 'parser' | 'autobot'

/** Сколько последних оповещений держим для баннеров (bounded — без роста 24/7). */
const MAX_ALERTS = 50

interface AppState {
  token: string | null
  authed: boolean
  /** Идёт восстановление сессии из localStorage на старте (показываем заставку, не форму). */
  restoring: boolean
  route: AppRoute
  engine: EngineState | null
  /** Список бирж из Registry (`GET /exchanges`) — для мультиселекта, не захардкожен. */
  exchanges: string[]
  wsStatus: WsStatus
  apiBusy: boolean
  /** Счётчик WS-событий движка: инкремент триггерит внеочередной /status-рефреш в «Авто-боте». */
  eventStamp: number
  /** Последние оповещения движка (для баннеров, дедуп по коду — последнее побеждает). */
  alerts: AlertItem[]

  login: (token: string) => Promise<{ ok: true } | { ok: false; error: string }>
  /** Восстановить сессию из localStorage на старте (валидирует токен запросом). */
  restoreSession: () => Promise<void>
  logout: () => void
  setRoute: (route: AppRoute) => void
  /** Применить свежий снимок состояния движка (из WS-снимка или периодического /status). */
  applyEngineState: (engine: EngineState) => void
  /** Снять баннер оповещения по коду. */
  dismissAlert: (code: string) => void
}

// --- Несреактивные синглтоны сессии ----------------------------------------
let socket: EngineSocket | null = null
let unsubActivity: (() => void) | null = null
// Время (по engine-часам) последнего применённого снимка состояния — монотонная защита
// от гонки WS-снимок ↔ /status-поллинг (запоздавший снимок не перетирает более свежий).
let lastSnapshotTs = 0

export const useAppStore = create<AppState>((set, get) => ({
  token: null,
  authed: false,
  // Если есть сохранённая сессия — стартуем в режиме восстановления (заставка вместо
  // мгновенной формы логина), иначе сразу форма.
  restoring: hasStoredSession(),
  // Восстанавливаем выбранную вкладку (рефреш не сбрасывает на «Парсинг»).
  route: loadRoute() === 'autobot' ? 'autobot' : 'parser',
  engine: null,
  exchanges: [],
  wsStatus: 'closed',
  apiBusy: false,
  eventStamp: 0,
  alerts: [],

  async login(token: string) {
    const trimmed = token.trim()
    if (!trimmed) return { ok: false as const, error: 'Введите токен' }

    setAuthToken(trimmed)
    // Валидируем токен реальным запросом до коммита сессии: 401 → токен неверен.
    try {
      // Список бирж — из Registry (GET /exchanges), не захардкожен (§1.5).
      const [engine, exchangesResp] = await Promise.all([getStatus(), getExchanges()])
      lastSnapshotTs = engine.snapshot_ts_ms // базовая отметка для монотонной защиты
      set({
        token: trimmed,
        authed: true,
        engine,
        exchanges: exchangesResp.exchanges.length ? exchangesResp.exchanges : engine.exchanges,
      })
    } catch (err) {
      setAuthToken(null)
      if (err instanceof ApiError) {
        if (err.status === 401) {
          clearSession() // токен отвергнут — стереть из браузера, чтобы не зацикливаться
          return { ok: false as const, error: 'Неверный токен' }
        }
        if (err.status === 429)
          return { ok: false as const, error: 'Слишком много попыток — подождите' }
        if (err.status === 503)
          return { ok: false as const, error: 'Движок недоступен (API без движка)' }
        if (err.status === 0)
          return { ok: false as const, error: 'API не отвечает — проверьте, запущен ли бэкенд' }
        return { ok: false as const, error: err.detail }
      }
      return { ok: false as const, error: 'Не удалось подключиться' }
    }

    // Подписка на активность API (мигание индикатора в шапке).
    unsubActivity?.()
    unsubActivity = subscribeActivity((busy) => set({ apiBusy: busy }))

    // WS: снимок состояния + поток событий движка.
    socket?.stop()
    socket = new EngineSocket({
      onSnapshot: (engine) => get().applyEngineState(engine),
      onStatus: (wsStatus) => set({ wsStatus }),
      onUnauthorized: () => get().logout(),
      // События движка для живого обновления вкладок «Авто-бота» (статус/связки):
      // на значимое событие триггерим один внеочередной /status-рефреш (хук слушает stamp);
      // оповещения (kind="alert") копим для баннеров (дедуп по коду — последнее побеждает).
      onEvent: (event) =>
        set((s) => {
          const next: Partial<AppState> = { eventStamp: s.eventStamp + 1 }
          if (event.kind === 'alert' && event.payload && typeof event.payload === 'object') {
            const p = event.payload as Record<string, unknown>
            // Нормализуем код в нижний регистр: бэкенд шлёт алерты и из `AlertCode.value`
            // (lowercase, health), и хардкод-строками UPPERCASE (engine/owner). Без нормализации
            // дедуп по коду и сопоставление с HEALTH_COVERED_CODES (lowercase) расходятся → дубли.
            const code = typeof p.code === 'string' ? p.code.toLowerCase() : null
            if (code) {
              const item: AlertItem = {
                code,
                severity: typeof p.severity === 'number' ? p.severity : 20,
                message: typeof p.message === 'string' ? p.message : code,
                ts_ms: typeof event.ts_ms === 'number' ? event.ts_ms : 0,
              }
              next.alerts = [item, ...s.alerts.filter((a) => a.code !== code)].slice(0, MAX_ALERTS)
            }
          }
          return next
        }),
      // События движка (этап 9 публикует api-активность/idle/etc.). На этапе 10
      // снимок состояния достаточен для шапки; детальный лог — этап 17.
    })
    socket.start()
    // Успешная авторизация → сохранить токен на сутки (рефреш не будет разлогинивать).
    saveSession(trimmed)
    return { ok: true as const }
  },

  async restoreSession() {
    const saved = loadSession()
    if (!saved) {
      set({ restoring: false })
      return
    }
    // Валидируем сохранённый токен реальным логином. Успех → сессия восстановлена;
    // 401 → login сам стёр сессию (токен мёртв); сеть/движок недоступны → токен сохраняем
    // (следующий рефреш при живом API авто-восстановит), показываем форму.
    try {
      await get().login(saved)
    } finally {
      set({ restoring: false })
    }
  },

  logout() {
    socket?.stop()
    socket = null
    unsubActivity?.()
    unsubActivity = null
    lastSnapshotTs = 0 // сброс монотонной отметки для следующей сессии
    setAuthToken(null)
    clearSession() // явный выход — забыть сохранённый токен
    set({
      token: null,
      authed: false,
      restoring: false,
      engine: null,
      exchanges: [],
      wsStatus: 'closed',
      apiBusy: false,
      eventStamp: 0,
      alerts: [],
      route: 'parser',
    })
  },

  setRoute(route: AppRoute) {
    set({ route })
    saveRoute(route) // запоминаем вкладку, чтобы рефреш её не сбрасывал
  },

  applyEngineState(engine: EngineState) {
    // Применяем только снимок не старше уже применённого (по engine-часам). И WS-снимок,
    // и периодический /status проходят сюда → запоздалый снимок любого источника не
    // перетирает более свежий (устраняет гонку WS↔/status).
    if (engine.snapshot_ts_ms < lastSnapshotTs) return
    lastSnapshotTs = engine.snapshot_ts_ms
    set({ engine })
  },

  dismissAlert(code: string) {
    set((s) => ({ alerts: s.alerts.filter((a) => a.code !== code) }))
  },
}))
